/**
 * Node visual de Notificação para o Flow Builder
 * Envia mensagem para N destinatários (não o contato do fluxo)
 * Exibe contagem de destinatários e preview resumido de cada um
 * 
 * RELEASE: 2.0.0 - 2026-03-12 - Suporte a múltiplos destinatários
 * RELEASE: 1.0.0 - 2026-03-12 - Implementação inicial
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Bell, Phone, Variable, User, Users, Briefcase } from 'lucide-react';
import {
  FlowNodeData,
  NotificationRecipient,
  NotificationRecipientMode,
  normalizeNotificationRecipients,
} from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Retorna ícone e label do modo de destinatário para exibição no node
 */
function getRecipientModeIcon(mode?: NotificationRecipientMode) {
  switch (mode) {
    case 'fixed':
      return { icon: Phone, label: 'Número fixo' };
    case 'variable':
      return { icon: Variable, label: 'Variável' };
    case 'system_user':
      return { icon: User, label: 'Usuário' };
    case 'crm_seller':
      return { icon: Users, label: 'Vendedor CRM' };
    case 'deal_seller':
      return { icon: Briefcase, label: 'Vendedor Negócio' };
    default:
      return { icon: Bell, label: '?' };
  }
}

/**
 * Retorna resumo curto de um destinatário para exibição no node
 */
function getRecipientSummary(r: NotificationRecipient): string {
  switch (r.mode) {
    case 'fixed':
      return r.phone || 'Telefone não definido';
    case 'variable':
      return r.variable ? `{{${r.variable}}}` : 'Variável não definida';
    case 'system_user':
      return r.user_name || 'Usuário não selecionado';
    case 'crm_seller':
      return r.seller_name || 'Vendedor não selecionado';
    case 'deal_seller':
      return 'Vendedor do negócio';
    default:
      return 'Não configurado';
  }
}

/**
 * Componente visual do node de notificação — suporta múltiplos destinatários
 */
export function NotificationNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config;

  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;

  // Normaliza destinatários (retrocompatível v1 → v2)
  const recipients = config ? normalizeNotificationRecipients(config as any) : [];

  // Preview do texto da mensagem (truncado)
  const messagePreview = config?.message_text
    ? config.message_text.length > 60
      ? config.message_text.substring(0, 60) + '...'
      : config.message_text
    : 'Sem mensagem';

  return (
    <div className="bg-card rounded-lg shadow-lg border-2 border-amber-500 min-w-[200px] max-w-[260px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />

      <Handle
        type="target"
        position={Position.Top}
        className="!bg-amber-500 !w-3 !h-3"
      />

      <div className="p-3">
        {/* Cabeçalho com ícone e título */}
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-amber-500/10 text-amber-600 p-2 rounded">
            <Bell className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {config?.nickname || 'Enviar Notificação'}
            </div>
          </div>
        </div>

        {/* Lista de destinatários */}
        {recipients.length === 0 ? (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mb-1.5">
            📞 Clique para configurar
          </div>
        ) : (
          <div className="space-y-1 mb-1.5">
            <div className="text-xs font-medium text-amber-700 dark:text-amber-400">
              {recipients.length} destinatário{recipients.length > 1 ? 's' : ''}:
            </div>
            {recipients.slice(0, 4).map((r, idx) => {
              const modeInfo = getRecipientModeIcon(r.mode);
              const ModeIcon = modeInfo.icon;
              return (
                <div key={idx} className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1">
                  <ModeIcon className="h-3 w-3 text-amber-600 shrink-0" />
                  <span className="truncate">{getRecipientSummary(r)}</span>
                </div>
              );
            })}
            {recipients.length > 4 && (
              <div className="text-[10px] text-muted-foreground pl-1">
                +{recipients.length - 4} mais...
              </div>
            )}
          </div>
        )}

        {/* Preview da mensagem */}
        <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
          💬 {messagePreview}
        </div>

        {/* Inbox usada */}
        {config?.inbox_name && (
          <div className="text-[10px] text-muted-foreground mt-1.5 truncate">
            📤 via {config.inbox_name}
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-amber-500 !w-3 !h-3"
      />
    </div>
  );
}
